import { useState, useEffect, useCallback } from 'react';
import { Venue, VenueAvailabilityResponse, AvailabilitySlot } from '../../types/venue';
import { apiRequest } from '../../utils/apiUtils';

interface UseVenueDetailReturn {
  venue: Venue | null;
  availabilitySlots: AvailabilitySlot[];
  loading: boolean;
  error: string | null;
  fetchVenue: () => Promise<void>;
  fetchAvailability: (date: string) => Promise<void>;
}

export const useVenueDetail = (venueId: string, slugPath?: string): UseVenueDetailReturn => {
  const [venue, setVenue] = useState<Venue | null>(null);
  const [availabilitySlots, setAvailabilitySlots] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVenue = useCallback(async () => {
    if (!venueId && !slugPath) return;

    setLoading(true);
    setError(null);

    try {
      const endpoint = slugPath
        ? `/venues/by-slug/${slugPath}`
        : `/venues/${venueId}`;

      const response = await apiRequest<Venue>(
        endpoint,
        { requireAuth: false }
      );

      if (response.data) {
        setVenue(response.data);
      } else {
        setError(response.error || 'Error al cargar la cancha');
      }
    } catch (err) {
      setError('Error de conexión al cargar la cancha');
    } finally {
      setLoading(false);
    }
  }, [venueId, slugPath]);

  const fetchAvailability = useCallback(async (date: string) => {
    if (!venueId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest<VenueAvailabilityResponse>(
        `/venues/${venueId}/availability?date=${date}`,
        { requireAuth: false }
      );

      if (response.data) {
        setAvailabilitySlots(response.data.slots);
      } else {
        setError(response.error || 'Error al cargar disponibilidad');
      }
    } catch (err) {
      setError('Error de conexión al cargar disponibilidad');
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  // Fetch venue details on mount
  useEffect(() => {
    fetchVenue();
  }, [fetchVenue]);

  return {
    venue,
    availabilitySlots,
    loading,
    error,
    fetchVenue,
    fetchAvailability,
  };
};
